import { Breadcrumb, Layout, Spin, Table, Tag, Button, Input, Select, Row, Col, Card, message, Space, Popconfirm, Form } from 'antd';
import { Content } from 'antd/es/layout/layout';
import Headerdiv from '../layout/header';
import Footerdiv from '../layout/footer';
import { useEffect, useState } from 'react';
import axiosClient from '../../api/axiosClient';
import { Link } from 'react-router-dom';
import { ClearOutlined, DeleteOutlined, EditOutlined, FilterOutlined, PlusOutlined, SearchOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { APPBACKGROUND_COLOR } from '../appsettings';

const { Option } = Select;

const initialFilters = {
    type: 'all',
    duration: 'all',
    status: 'all',
    search: '',
};

const SalesItems = () => {
    const [form] = Form.useForm();

    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    const [filters, setFilters] = useState(initialFilters);

    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
    });

    const USERID = localStorage.getItem('USERID');

    /** Fetch sales items list */
    const fetchSalesItems = async (page = 1, pageSize = 10) => {
        setLoading(true);
        try {
            const params = new URLSearchParams({
                page,
                pageSize,
                ...filters,
            });

            const response = await axiosClient.get(`/sales/get_all_sales_items.php?${params.toString()}`);
            const result = response.data;

            if (result.status === 'success') {
                setData(result.data);
                setPagination({
                    current: result.page,
                    pageSize: result.pageSize,
                    total: result.total,
                });
            } else {
                message.error(result.message || 'Failed to fetch sales items.');
            }
        } catch (error) {
            console.error('Fetch error:', error);
            message.error(error.response?.data?.message || 'Network error.');
        } finally {
            setLoading(false);
        }
    };

    /** Load data on mount */
    useEffect(() => {
        fetchSalesItems(1, 10);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /** Table pagination handlers */
    const handleTableChange = (newPagination) => {
        fetchSalesItems(newPagination.current, newPagination.pageSize);
    };

    /** Clear filter */
    const clearFilters = () => {
        form.resetFields();
        setFilters(initialFilters);
        setPagination({ current: 1, pageSize: 10, total: 0 });
        fetchSalesItems(1, 10);
    };

    /** Delete item */
    const handleDelete = async (id) => {
        try {
            const response = await axiosClient.post(`/sales/delete_sales_item_by_id.php`, {
                id,
                by: USERID
            });

            const result = response.data;
            if (result.status === 'success') {
                message.success(result.message);
                fetchSalesItems(1, pagination.pageSize);
            } else {
                message.error(result.message);
            }
        } catch (error) {
            console.error(error);
            message.error('Network error.');
        }
    };

    /** Table Columns */
    const columns = [
        { title: 'Code', dataIndex: 'sp_code', width: 80 },
        { title: 'Title', dataIndex: 'sp_title' },
        { 
            title: 'Type', 
            dataIndex: 'sp_type',
            render: (type) => (
                <Tag color={type === 'PACKAGE' ? 'purple' : 'blue'}>
                    {type}
                </Tag>
            ),
        },
        { 
            title: 'Duration', 
            dataIndex: 'sp_duration',
            render: (duration) => {
                const colors = { ONETIME: 'green', DAILY: 'orange', MONTHLY: 'cyan' };
                return <Tag color={colors[duration] || 'default'}>{duration}</Tag>;
            },
        },
        { 
            title: 'Price', 
            dataIndex: 'sp_uprice',
            render: (price) => `Rs. ${Number(price).toLocaleString()}`,
        },
        {
            title: 'Status',
            dataIndex: 'sp_active',
            render: (status) => (
                <Tag color={status ? 'green' : 'red'}>
                    {status ? 'ACTIVE' : 'INACTIVE'}
                </Tag>
            ),
        },
        {
            title: 'Created',
            dataIndex: 'sp_cr_dt',
            render: (date) => date ? dayjs(date).format('DD MMM YYYY hh:mm A') : 'N/A',
        },
        {
            title: 'Action',
            render: (_, record) => (
                <Space>
                    <Button
                        style={{ backgroundColor: "#FFC107", borderColor: "#FFC107", color: "white" }}
                        type="primary"
                        size="small"
                    >
                        <Link to={`/sales/edit/${record.sp_id}`}>
                            <EditOutlined />
                        </Link>
                    </Button>

                    <Popconfirm
                        title="Are you sure to delete this item?"
                        onConfirm={() => handleDelete(record.sp_id)}
                        okText="Yes"
                        cancelText="No"
                    >
                        <Button icon={<DeleteOutlined />} type="primary" danger size="small" />
                    </Popconfirm>
                </Space>
            )
        }
    ];

    return (
        <Spin spinning={loading} tip="Loading Sales Items...">
            <Layout style={{ backgroundColor: APPBACKGROUND_COLOR, minHeight: '100vh' }}>
                <Headerdiv />

                <Content style={{ padding: '10px' }}>
                    <Breadcrumb style={{ marginBottom: 10 }}>
                        <Breadcrumb.Item><Link to="/sales/items">Sales</Link></Breadcrumb.Item>
                        <Breadcrumb.Item>Sales Items</Breadcrumb.Item>
                    </Breadcrumb>

                    {/* FILTER CARD */}
                    <Card
                        style={{ marginBottom: "15px" }}
                        title="Filter"
                        extra={
                            <Row style={{ float: "right" }} gutter={[10, 10]} align="middle">
                                <Col>
                                    <Link to="/sales/add">
                                        <Button icon={<PlusOutlined />} type="primary">Add Sales Item</Button>
                                    </Link>
                                </Col>
                            </Row>
                        }
                    >
                        <Form
                            form={form}
                            layout="vertical"
                            initialValues={initialFilters}
                            onValuesChange={(_, allValues) => setFilters(allValues)}
                            onFinish={() => fetchSalesItems(1, pagination.pageSize)}
                        >
                            <Row gutter={[10, 10]} align="middle">

                                <Col xs={24} sm={12} md={6}>
                                    <Form.Item name="search" label="Search">
                                        <Input
                                            placeholder="Search by Code or Title"
                                            prefix={<SearchOutlined />}
                                            allowClear
                                        />
                                    </Form.Item>
                                </Col>

                                <Col xs={12} sm={6} md={4}>
                                    <Form.Item name="type" label="Type">
                                        <Select>
                                            <Option value="all">All Types</Option>
                                            <Option value="ITEM">Item</Option>
                                            <Option value="PACKAGE">Package</Option>
                                        </Select>
                                    </Form.Item>
                                </Col>

                                <Col xs={12} sm={6} md={4}>
                                    <Form.Item name="duration" label="Duration">
                                        <Select>
                                            <Option value="all">All Duration</Option>
                                            <Option value="ONETIME">One Time</Option>
                                            <Option value="DAILY">Daily</Option>
                                            <Option value="MONTHLY">Monthly</Option>
                                        </Select>
                                    </Form.Item>
                                </Col>

                                <Col xs={12} sm={6} md={4}>
                                    <Form.Item name="status" label="Status">
                                        <Select>
                                            <Option value="all">All Status</Option>
                                            <Option value="active">Active</Option>
                                            <Option value="inactive">Inactive</Option>
                                        </Select>
                                    </Form.Item>
                                </Col>

                                <Col xs={12} sm={6} md={3}>
                                    <Button type="primary" block icon={<FilterOutlined />} htmlType="submit">
                                        Filter
                                    </Button>
                                </Col>

                                <Col xs={12} sm={6} md={3}>
                                    <Button block icon={<ClearOutlined />} onClick={clearFilters}>
                                        Clear
                                    </Button>
                                </Col>

                            </Row>
                        </Form>
                    </Card>

                    {/* TABLE */}
                    <Card title="Sales Items">
                        <Table
                            columns={columns}
                            dataSource={data}
                            rowKey="sp_id"
                            pagination={pagination}
                            onChange={handleTableChange}
                            bordered
                            size="small"
                            scroll={{ x: 900 }}
                        />
                    </Card>
                </Content>

                <Footerdiv />
            </Layout>
        </Spin>
    );
};

export default SalesItems;
